import type { HTMLAttributes, ReactNode } from "react";
import { Card, type CardProps } from "./card";
import { Hint } from "./tooltip";

export interface SectionCardProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
	title?: ReactNode;
	/**
	 * `page` — 24px, заголовок раздела витрины («Points», «Security»);
	 * `card` — 20px, заголовок карточки внутри раздела («Plans», «Your points»).
	 */
	level?: "page" | "card";
	/** Текст подсказки у знака вопроса рядом с заголовком. */
	hint?: ReactNode;
	/**
	 * Доступное имя знака вопроса («Подробнее»). Обязательно вместе с `hint`:
	 * умолчаний текста у кита нет, и без подписи знак просто не рисуется.
	 */
	hintTriggerLabel?: string;
	/** Правый слот шапки: «See all», чип статуса, счётчик с прогрессом. */
	action?: ReactNode;
	padding?: CardProps["padding"];
	gap?: CardProps["gap"];
	floating?: boolean;
	children?: ReactNode;
}

/**
 * Белая карточка с типовой шапкой: заголовок, знак вопроса, правое действие.
 * Шапка выровнена по верху, а не по центру: справа часто стоит блок из двух
 * строк («5 / 6» + прогресс), и центрирование увело бы заголовок вниз.
 */
export function SectionCard({
	title,
	level = "card",
	hint,
	hintTriggerLabel,
	action,
	padding,
	gap,
	floating,
	className,
	children,
	...rest
}: SectionCardProps) {
	const hasHeader = title != null || action != null;
	return (
		<Card padding={padding} gap={gap} floating={floating} className={className} {...rest}>
			{hasHeader ? (
				<header className="flex flex-wrap items-start justify-between gap-x-5 gap-y-2">
					<div className="flex min-w-0 flex-1 items-center gap-2">
						{title != null ? (
							<h2 className={level === "page" ? "t-section" : "t-subsection"}>{title}</h2>
						) : null}
						{hint != null && hintTriggerLabel != null ? (
							// Знак опущен к оптической середине СТРОЧНЫХ букв, а не заглавных.
							// `items-center` ставит его по центру всей строки, то есть по
							// центру заглавной, и в крупном заголовке (20–24px) он заметно
							// висел выше текста. Сдвиг в `em` — от кегля заголовка: разница
							// между высотой заглавной и строчной у Geist ≈ 0.2em, половина
							// её и есть смещение. Margin, а не `translate`: transform на
							// кнопке уже занят `iec-press-glyph`.
							<Hint label={hint} triggerLabel={hintTriggerLabel} className="mt-[0.1em]" />
						) : null}
					</div>
					{action != null ? (
						<div className="flex max-w-full shrink-0 items-center gap-2">{action}</div>
					) : null}
				</header>
			) : null}
			{children}
		</Card>
	);
}
